import React, { useState, useEffect, useRef } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Icon } from "@vds/icons";
import { Button } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import { Badge } from "@vds/badges";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { IconButton } from "@vds/core";
import {
  APPEARANCE_TEXT,
  CREATE_APPEARANCE,
  PLACEHOLDER_IMAGE,
  IMAGE_GUIDELINES_TEXT,
  APPEARANCE_RUNTIME_STATUS
} from "../../../constants/enrolledAppearanceConstants";
import { Line } from "@vds/lines";
import { uploadAppearanceImageRequest, deleteAppearanceImageRequest, downloadAppearanceImagesRequest } from "../../../store/actions/ea-actions";
import ErrorModal from "./ErrorModal";
import ImageGuidelinesModal from "./ImageGuidelinesModal";
import styles from "./UploadAppearance.module.css";
import {
  FrameForDragAndDrop,
  FrameForFileUploader,
  FrameForImageDesc,
  FrameForUploadIcon,
  ImageDescription,
  InnerFrameForDragAndDrop,
  TextAppearance,
  ImageCardContainer,
  UploadText,
  ImageCardItem,
  ImageCardContent,
  ImageCardDeleteButtonContainer,
  ImageCardInner,
  CardImageContainer,
  CardImage,
  CardFileName,
  CardValidationStatus,
  CardValidationDesc,
  SectionLabel,
  UploadHeaderRow
} from "../EACreate/ea-create-styles";
import { IconButtons, LoaderContainer, ImageStatusIconAdd as ImageStatusIcon } from "../EAList/styles/ea-styles";
import { ImageDownloadErrorWrapper, ImageDownloadErrorContent, ImageDownloadErrorRefresh } from "./ImageDownloadError";

const DeleteIcon = () => (
  <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.0463 16.6667H11.463V4.16667H1.0463V16.6667ZM2.08333 5.21296H10.4167V15.6296H2.08333V5.21296ZM8.33333 2.08333V0H4.16667V2.08333H0V3.12963H12.5V2.08333H8.33333ZM7.2963 2.08333H5.21296V1.0463H7.2963V2.08333ZM3.87963 13.5463H4.91667V7.2963H3.87963V13.5463ZM7.58333 13.5463H8.62037V7.2963H7.58333V13.5463Z"
      fill="black"
    />
  </svg>
);

const UploadAppearance = ({ appearanceId, isEditMode, images }) => {
  const dispatch = useDispatch();
  const [validationErrors, setValidationErrors] = useState({});
  const { imageUploadStatus, imageDeleteStatus, imageCache, uploadCount, loading } = useSelector((state) => state.enrolledAppearanceReducer);
  const imageLoadingStateForAppearance = useSelector((state) => state.enrolledAppearanceReducer.imageLoadingStates[appearanceId], shallowEqual);
  const isAppearanceLoading = imageLoadingStateForAppearance?.loading ?? false;
  const imageCacheForAppearance = useSelector((state) => state.enrolledAppearanceReducer.imageCache[appearanceId], shallowEqual);
  const fileTypes = ["JPG", "PNG", "JPEG"];
  const doCaptions = [CREATE_APPEARANCE.DO_CAPTION_1, CREATE_APPEARANCE.DO_CAPTION_2];
  const dontCaptions = [CREATE_APPEARANCE.DONT_CAPTION_1, CREATE_APPEARANCE.DONT_CAPTION_2, CREATE_APPEARANCE.DONT_CAPTION_3];
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorConfig, setErrorConfig] = useState({
    title: "",
    body: ""
  });
  const [localFiles, setLocalFiles] = useState([]);
  const imageContainerRef = useRef(null);
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);

  const mimeTypeMap = {
    "image/jpeg": ["JPEG", "JPG"],
    "image/png": ["PNG"]
  };

  const maxFileSizeMB = CREATE_APPEARANCE.MAX_FILE_SIZE_MB;
  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

  const isValidFileType = (file) => {
    const mimeType = file.type.toLowerCase();
    const allowedExtensions = mimeTypeMap[mimeType];
    return allowedExtensions && allowedExtensions.some((ext) => fileTypes.includes(ext));
  };

  const isValidFileSize = (file) => {
    return file.size <= maxFileSizeBytes;
  };

  const showInvalidFileTypeError = () => {
    setShowErrorModal(true);
    setErrorConfig({
      title: CREATE_APPEARANCE.INVALID_FILE_TYPE,
      body: CREATE_APPEARANCE.INVALID_FILE_DESC.replace("[placeholder]", fileTypes.join(", "))
    });
  };

  const showFileSizeTooLargeError = () => {
    setShowErrorModal(true);
    setErrorConfig({
      title: CREATE_APPEARANCE.FILE_SIZE_ERROR,
      body: CREATE_APPEARANCE.IMAGE_SIZE_TOO_LARGE.replace("[placeholder]", String(maxFileSizeMB))
    });
  };

  const createErrorDesc = (fileData, isDeleteError = false, errorTitle = null) => {
    let errorMessage,
      imageTooLarge = false;

    if (isDeleteError) {
      errorMessage = CREATE_APPEARANCE.DELETE_IMAGE_ERROR;
    } else {
      // Check if errorTitle contains 413 (Payload Too Large)
      if (errorTitle && errorTitle.includes("413")) {
        errorMessage = CREATE_APPEARANCE.IMAGE_SIZE_TOO_LARGE_TITLE;
        imageTooLarge = true;
      } else {
        errorMessage = CREATE_APPEARANCE.UPLOAD_IMAGE_ERROR;
      }
    }

    const handleRetryAction = isDeleteError ? () => handleRetryDelete(fileData) : () => handleRetryUpload(fileData);

    return (
      <CardValidationDesc onClick={handleRetryAction}>
        {errorMessage} &nbsp;
        {!imageTooLarge && <Icon name="sync" id="retry_icon" size="small" color="#000000" />}
      </CardValidationDesc>
    );
  };

  const createFileData = async (file, fileId, downloadedImg) => {
    let imageUrl, originalFile, id;

    if (downloadedImg) {
      const { blobUrl, imageId, imageName } = downloadedImg;

      // Use downloaded blob URL or placeholder if download failed
      imageUrl = blobUrl || PLACEHOLDER_IMAGE;
      originalFile = {
        name: imageName || `image-${imageId}.jpg`,
        type: "image/jpeg",
        size: 0
      };
      id = imageId;
    } else {
      // Handle regular file upload
      const blob = file.slice(0, file.size, file.type);
      imageUrl = URL.createObjectURL(blob);
      originalFile = file;
      id = "";
    }

    const uploading = !(isEditMode || downloadedImg);

    return {
      fileId,
      appearanceId,
      originalFile,
      id,
      imageUrl,
      uploading,
      uploadError: false,
      errorTitle: null,
      uploadErrorDesc: null,
      imageStatus: downloadedImg?.status || null
    };
  };

  const dispatchUploadRequest = (appearanceId, file, fileId, imageUrl) => {
    dispatch(
      uploadAppearanceImageRequest({
        appearanceId,
        file,
        fileId,
        imageUrl
      })
    );
  };

  const handleFileUpload = async (file) => {
    if (!file) {
      console.error("No files selected.");
      return;
    }

    if (!isValidFileType(file)) {
      showInvalidFileTypeError();
      return;
    }

    if (!isValidFileSize(file)) {
      showFileSizeTooLargeError();
      return;
    }

    try {
      const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const fileData = await createFileData(file, fileId);

      dispatchUploadRequest(fileData.appearanceId, file, fileData.fileId, fileData.imageUrl);
      setLocalFiles((prevFiles) => [...prevFiles, fileData]);
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  const handleRetryUpload = (fileData) => {
    dispatch(
      uploadAppearanceImageRequest({
        appearanceId: fileData.appearanceId,
        file: fileData.originalFile,
        fileId: fileData.fileId
      })
    );
  };

  const handleRetryDelete = (fileData) => {
    dispatch(
      deleteAppearanceImageRequest({
        imageId: fileData.id,
        appearanceId: fileData.appearanceId
      })
    );
  };

  const handleDeleteImage = (fileData) => {
    // Only dispatch if image has an id (already uploaded to API)
    if (fileData.id) {
      dispatch(
        deleteAppearanceImageRequest({
          imageId: fileData.id,
          appearanceId: appearanceId
        })
      );
    } else {
      // Upload failed or pending — remove by fileId (index-based filter is unsafe across sections)
      setLocalFiles((prevFiles) => prevFiles.filter((f) => f.fileId !== fileData.fileId));
    }
  };

  const updateDonwloadedFilesToState = async (downloadedImgs = []) => {
    const downloadedFiles = [];
    if (Object.keys(downloadedImgs).length) {
      for (let [imageId, img] of Object.entries(downloadedImgs)) {
        const { blobUrl, status, imageName } = img;
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const fileData = await createFileData(null, fileId, { blobUrl, imageId, status, imageName });
        downloadedFiles.push(fileData);
      }
    } else {
      for (let image of images) {
        const { imageId = "", status = "", imageName = "" } = image;
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const fileData = await createFileData(null, fileId, { blobUrl: null, imageId, status, imageName });
        downloadedFiles.push({ ...fileData, downloadFailed: true });
      }
    }

    setLocalFiles(() => [...downloadedFiles]);
  };

  useEffect(() => {
    setLocalFiles((prevFiles) =>
      prevFiles.map((file) => {
        const uploadStatus = imageUploadStatus[file.fileId];
        if (uploadStatus) {
          if (uploadStatus.status === "uploading" && imageContainerRef.current) {
            setTimeout(() => {
              imageContainerRef.current?.scrollTo({
                top: imageContainerRef.current.scrollHeight,
                behavior: "smooth"
              });
            }, 10);
          }

          const errorTitle = uploadStatus.error || "";
          return {
            ...file,
            uploading: ["error", "success"].includes(uploadStatus.status) ? false : true,
            uploadError: uploadStatus.status === "error",
            id: uploadStatus.imageId || "",
            // Persist imageStatus from upload API response; fall back to existing value if not yet available
            imageStatus: uploadStatus.status === "success" ? (uploadStatus.imageStatus ?? file.imageStatus) : file.imageStatus,
            originalFile:
              uploadStatus.status === "success" && uploadStatus.imageId
                ? { ...file.originalFile, name: uploadStatus.imageName || `image-${uploadStatus.imageId}.jpg` }
                : file.originalFile,
            errorTitle,
            uploadErrorDesc: uploadStatus.status === "error" ? createErrorDesc(file, false, errorTitle) : null
          };
        }
        return file;
      })
    );
  }, [imageUploadStatus]);

  useEffect(() => {
    setLocalFiles((prevFiles) =>
      prevFiles
        .map((file) => {
          const deleteStatus = imageDeleteStatus[file.id];
          if (deleteStatus?.status === "deleted") {
            // Remove file after successful deletion
            if (file.imageUrl) {
              URL.revokeObjectURL(file.imageUrl);
            }
            return null;
          }
          if (deleteStatus?.status === "error") {
            return {
              ...file,
              deleting: false,
              deleteError: true,
              deleteErrorDesc: createErrorDesc(file, true),
              errorTitle: deleteStatus.error || ""
            };
          }
          if (deleteStatus?.status === "deleting") {
            return {
              ...file,
              deleting: true
            };
          }
          return file;
        })
        .filter((file) => file)
    );
  }, [imageDeleteStatus]);

  // this useEffect fires for edit mode only
  useEffect(() => {
    if (isEditMode && appearanceId && images?.length) {
      const cacheForAppearance = imageCache[appearanceId] || {};
      // Identify images that are not yet present in the cache (newly uploaded or not downloaded yet)
      const missingImages = images.filter((img) => {
        const key = img?.imageId;
        return key ? !cacheForAppearance[key] : true;
      });

      // If there are missing images and we're not already loading this appearance, dispatch download for just the missing ones
      if (missingImages?.length > 0 && !imageLoadingStateForAppearance) {
        dispatch(downloadAppearanceImagesRequest(appearanceId, missingImages));
      }
    }
  }, [isEditMode, appearanceId, images.length]);

  // Re-syncs imageStatus when FETCH_APPEARANCES_SUCCESS updates the cache (e.g. navigating back to edit
  // while a list fetch is in-flight). Only patches status — does not replace blob URLs or re-download.
  useEffect(() => {
    if (!imageCacheForAppearance) return;
    setLocalFiles((prev) => {
      if (!prev.length) return prev;
      return prev.map((f) => {
        const cached = imageCacheForAppearance[f.id];
        return cached ? { ...f, imageStatus: cached.status } : f;
      });
    });
  }, [imageCacheForAppearance]);

  // this useEffect fires irrespective of edit or create mode, when appearance image is downloading
  useEffect(() => {
    if (imageLoadingStateForAppearance?.loading) return;
    const imagesForCurrentAppearance = imageCache[appearanceId];
    if (imagesForCurrentAppearance && Object.keys(imagesForCurrentAppearance).length) {
      updateDonwloadedFilesToState(imagesForCurrentAppearance);
    } else if (imageLoadingStateForAppearance?.error) {
      updateDonwloadedFilesToState([]);
    }
  }, [imageLoadingStateForAppearance, appearanceId]);

  const renderImageCard = (file, variant) => {
    const { originalFile, imageUrl, uploadError, uploadErrorDesc, deleteError, deleteErrorDesc, deleting } = file;

    const getImageContent = () => {
      if (file.downloadFailed) {
        return (
          <CardImageContainer>
            <ImageDownloadErrorWrapper $width={81} $height={81}>
              <ImageDownloadErrorContent>
                <Icon name="error" size="small" color="#000000" />
                <span>{APPEARANCE_TEXT.IMAGE_FAILED_TO_LOAD}</span>
              </ImageDownloadErrorContent>
            </ImageDownloadErrorWrapper>
            <ImageDownloadErrorRefresh
              onClick={() =>
                dispatch(
                  downloadAppearanceImagesRequest(
                    appearanceId,
                    images.filter((img) => img.imageId === file.id)
                  )
                )
              }
            >
              <Icon name="sync" size="small" color="#000000" />
            </ImageDownloadErrorRefresh>
          </CardImageContainer>
        );
      }
      if (variant === "uploading") return <CardImage src={imageUrl} />;
      const iconByVariant = {
        invalid: { name: "close", status: APPEARANCE_RUNTIME_STATUS.INVALID },
        validating: { name: "timer", status: APPEARANCE_RUNTIME_STATUS.VALIDATING },
        valid: { name: "checkmark", status: APPEARANCE_RUNTIME_STATUS.VALID }
      };
      const icon = iconByVariant[variant];
      return (
        <CardImageContainer>
          <CardImage src={imageUrl} />
          {icon && (
            <ImageStatusIcon status={icon.status}>
              <Icon name={icon.name} color="white" ariaHidden={true} />
            </ImageStatusIcon>
          )}
        </CardImageContainer>
      );
    };

    return (
      <ImageCardItem key={file.fileId}>
        <ImageCardContent>
          <ImageCardInner>
            {getImageContent()}
            <CardFileName>{originalFile.name}</CardFileName>
            {(variant === "failed" || variant === "invalid") && uploadError && <CardValidationStatus>{uploadErrorDesc}</CardValidationStatus>}
            {variant !== "uploading" && deleteError && <CardValidationStatus>{deleteErrorDesc}</CardValidationStatus>}
          </ImageCardInner>
        </ImageCardContent>
        <ImageCardDeleteButtonContainer>
          <IconButtons onClick={() => handleDeleteImage(file)} data-testid="delete-appearance-button" title={APPEARANCE_TEXT.DELETE_APPEARANCE_TITLE}>
            <DeleteIcon />
          </IconButtons>
        </ImageCardDeleteButtonContainer>
        {variant === "uploading" && <Loader active fullscreen={false} showOverlay={true} />}
        {variant !== "uploading" && deleting && <Loader active fullscreen={false} showOverlay={true} />}
      </ImageCardItem>
    );
  };

  return (
    <>
      {showErrorModal && (
        <ErrorModal
          opened={showErrorModal}
          title={errorConfig.title}
          body={errorConfig.body}
          footerButtonText={CREATE_APPEARANCE.CLOSE}
          onClose={() => setShowErrorModal(false)}
          ariaLabel="File size error modal"
        />
      )}
      <UploadHeaderRow>
        <TextAppearance isEditMode={isEditMode}>{isEditMode ? CREATE_APPEARANCE.UPLOAD_DESCRIPTION_EDIT : CREATE_APPEARANCE.UPLOAD_DESCRIPTION}</TextAppearance>
        <IconButton
          id={styles.supportIconButton}
          ariaLabel={IMAGE_GUIDELINES_TEXT.ARIA_OPEN_MODAL}
          kind="highContrast"
          onClick={() => setIsGuidelinesOpen(true)}
          renderIcon={(props) => <Icon name="support-drawer" {...props} />}
          size="small"
          elevation="none"
          focusBorderPosition="outside"
          iconOffset={{ x: 0, y: 0 }}
          fitToIcon={false}
        />
      </UploadHeaderRow>
      {isAppearanceLoading && <Loader active={true} />}
      <FrameForDragAndDrop>
        <InnerFrameForDragAndDrop>
          <FileUploader multiple={false} onTypeError={showInvalidFileTypeError} handleChange={handleFileUpload} name="files" types={fileTypes}>
            <FrameForFileUploader>
              <FrameForUploadIcon>
                <Icon name="upload" size="large" color="#000000" />
              </FrameForUploadIcon>
              <UploadText>{CREATE_APPEARANCE.DRAG_TO_UPLOAD}</UploadText>
              <Button surface="light" use="secondary">
                {uploadCount ? CREATE_APPEARANCE.ADD_MORE_TXT : CREATE_APPEARANCE.BROWSE_TXT}
              </Button>
            </FrameForFileUploader>
          </FileUploader>
          <FrameForImageDesc>
            <ImageDescription>{CREATE_APPEARANCE.MAX_FILE_SIZE_DESC.replace("[placeholder]", String(maxFileSizeMB))}</ImageDescription>
            <ImageDescription>{CREATE_APPEARANCE.SUPPORTED_FILE_TYPES}</ImageDescription>
          </FrameForImageDesc>
        </InnerFrameForDragAndDrop>
      </FrameForDragAndDrop>
      {loading && (
        <LoaderContainer>
          <Loader active={true} />
        </LoaderContainer>
      )}
      {!loading &&
        (() => {
          const uploadingFiles = localFiles.filter((f) => f.uploading);
          const isInvalidStatus = (status) => status === APPEARANCE_RUNTIME_STATUS.INVALID || status === APPEARANCE_RUNTIME_STATUS.FAILED;
          const uploadFailedFiles = localFiles.filter((f) => !f.uploading && f.uploadError);
          const validatingFiles = localFiles.filter((f) => !f.uploading && !f.uploadError && f.imageStatus === APPEARANCE_RUNTIME_STATUS.VALIDATING);
          const invalidFiles = localFiles.filter((f) => !f.uploading && !f.uploadError && isInvalidStatus(f.imageStatus));
          const validFiles = localFiles.filter(
            (f) =>
              !f.uploading &&
              !f.uploadError &&
              !isInvalidStatus(f.imageStatus) &&
              f.imageStatus !== APPEARANCE_RUNTIME_STATUS.VALIDATING &&
              f.imageStatus !== null
          );
          const sections = [
            { files: uploadingFiles, label: CREATE_APPEARANCE.UPLOADING_IMAGES_LABEL, variant: "uploading" },
            { files: uploadFailedFiles, label: CREATE_APPEARANCE.FAILED_UPLOAD_IMAGES_LABEL, variant: "failed" },
            { files: validatingFiles, label: CREATE_APPEARANCE.VALIDATING_IMAGES_LABEL, variant: "validating" },
            { files: invalidFiles, label: CREATE_APPEARANCE.INVALID_IMAGES_LABEL, variant: "invalid" },
            { files: validFiles, label: CREATE_APPEARANCE.VALID_IMAGES_LABEL, variant: "valid", ref: imageContainerRef }
          ];
          const activeSections = sections.filter((s) => s.files.length > 0).length;
          const hasMultipleSections = activeSections > 1;
          return (
            <>
              {sections.map((section, i) => {
                if (!section.files.length) return null;
                const hasNextSection = sections.slice(i + 1).some((s) => s.files.length > 0);
                return (
                  <React.Fragment key={section.variant}>
                    <SectionLabel>{section.label}</SectionLabel>
                    <ImageCardContainer ref={section.ref}>{section.files.map((file) => renderImageCard(file, section.variant))}</ImageCardContainer>
                    {hasMultipleSections && hasNextSection && <Line type="primary" className={styles.sectionDivider} />}
                  </React.Fragment>
                );
              })}
            </>
          );
        })()}

      <ImageGuidelinesModal opened={isGuidelinesOpen} onClose={() => setIsGuidelinesOpen(false)} />
    </>
  );
};

export default UploadAppearance;
